import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { AppStore } from "@/store";
import { api } from "@/modules/api";
import { logger } from "@/modules/logger";
import { localStorageModule } from "@/modules/local-storage";
import { objectModule } from "@/modules/object";
import { toastModule } from "@/modules/toast";
import { windowModule } from "@/modules/window";

interface AssistantApiResponse<ResponseData> {
  data?: ResponseData;
  error?: unknown;
}

interface AssistantApiClient {
  get<ResponseData>(
    path: string,
    options?: Record<string, unknown>
  ): Promise<AssistantApiResponse<ResponseData>>;
  post<ResponseData>(
    path: string,
    options?: Record<string, unknown>
  ): Promise<AssistantApiResponse<ResponseData>>;
  patch<ResponseData>(
    path: string,
    options?: Record<string, unknown>
  ): Promise<AssistantApiResponse<ResponseData>>;
}

export interface MemAgentAssistant {
  id: string;
  space_account_id: string;
  slug: string;
  is_enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface ListAssistantsResponse {
  results: MemAgentAssistant[];
  total: number;
}

interface SlackConnectionSettingsResponse {
  is_connected: boolean;
  is_enabled: boolean;
  slack_workspace_id: string | null;
  slack_team_name: string | null;
  connected_at: string | null;
}

interface SlackConnectResponse {
  authorization_url: string;
  state: string;
}

const assistantApi = api as unknown as AssistantApiClient;

const buildDisconnectedSlackSettings = (): SlackConnectionSettingsResponse => ({
  is_connected: false,
  is_enabled: false,
  slack_workspace_id: null,
  slack_team_name: null,
  connected_at: null,
});

const getResponseErrorMessage = ({
  error,
  fallback,
}: {
  error: unknown;
  fallback: string;
}): string => {
  if (error && typeof error === "object" && "detail" in error) {
    const detail = (error as { detail?: unknown }).detail;
    if (typeof detail === "string" && detail.length > 0) {
      return detail;
    }
  }

  return fallback;
};

export class AppStoreMemAgentStore {
  private ready = false;
  private loading = false;
  private settingUp = false;
  private updatingAssistant = false;
  private connectingSlack = false;
  private updatingSlack = false;
  private assistant: MemAgentAssistant | null = null;
  private slackSettings: SlackConnectionSettingsResponse = buildDisconnectedSlackSettings();

  constructor(private readonly store: AppStore) {
    makeObservable<
      AppStoreMemAgentStore,
      | "ready"
      | "loading"
      | "settingUp"
      | "updatingAssistant"
      | "connectingSlack"
      | "updatingSlack"
      | "assistant"
      | "slackSettings"
      | "fetchSlackSettings"
    >(this, {
      ready: observable,
      loading: observable,
      settingUp: observable,
      updatingAssistant: observable,
      connectingSlack: observable,
      updatingSlack: observable,
      assistant: observable,
      slackSettings: observable,
      currentAssistant: computed,
      isReady: computed,
      isLoading: computed,
      isSettingUp: computed,
      isUpdatingAssistant: computed,
      isConnectingSlack: computed,
      isUpdatingSlack: computed,
      hasAssistant: computed,
      isAssistantEnabled: computed,
      assistantSlug: computed,
      isSlackConnected: computed,
      isSlackEnabled: computed,
      slackTeamName: computed,
      load: action,
      reload: action,
      setup: action,
      setAssistantEnabled: action,
      connectSlack: action,
      setSlackEnabled: action,
      fetchSlackSettings: false,
    });
  }

  get currentAssistant(): MemAgentAssistant | null {
    return this.assistant;
  }

  get isReady(): boolean {
    return this.ready;
  }

  get isLoading(): boolean {
    return this.loading;
  }

  get isSettingUp(): boolean {
    return this.settingUp;
  }

  get isUpdatingAssistant(): boolean {
    return this.updatingAssistant;
  }

  get isConnectingSlack(): boolean {
    return this.connectingSlack;
  }

  get isUpdatingSlack(): boolean {
    return this.updatingSlack;
  }

  get hasAssistant(): boolean {
    return this.assistant !== null;
  }

  get isAssistantEnabled(): boolean {
    return this.assistant?.is_enabled ?? false;
  }

  get assistantSlug(): string | null {
    return this.assistant?.slug ?? null;
  }

  get isSlackConnected(): boolean {
    return this.slackSettings.is_connected;
  }

  get isSlackEnabled(): boolean {
    return this.slackSettings.is_enabled;
  }

  get slackTeamName(): string | null {
    return this.slackSettings.slack_team_name;
  }

  load = async (): Promise<void> => {
    if (this.loading) return;

    runInAction(() => {
      this.loading = true;
    });

    try {
      const response = await assistantApi.get<ListAssistantsResponse>("/v2/assistants", {});

      if (!response.data || response.error) {
        throw new Error(
          getResponseErrorMessage({
            error: response.error,
            fallback: "Failed to load Mem Agent settings",
          })
        );
      }

      const assistant = response.data.results[0] ?? null;
      const slackSettings = assistant
        ? await this.fetchSlackSettings({ assistantId: assistant.id })
        : buildDisconnectedSlackSettings();

      runInAction(() => {
        this.assistant = assistant;
        this.slackSettings = slackSettings;
      });
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreMemAgentStore] Failed to load Mem Agent settings",
        info: { error: objectModule.safeErrorAsJson(error) },
      });
    } finally {
      runInAction(() => {
        this.loading = false;
        this.ready = true;
      });
    }
  };

  reload = async (): Promise<void> => {
    await this.load();
  };

  setup = async (): Promise<void> => {
    if (this.settingUp) return;

    runInAction(() => {
      this.settingUp = true;
    });

    try {
      const response = await assistantApi.post<MemAgentAssistant>("/v2/assistants", {
        body: {
          is_enabled: true,
        },
      });

      if (!response.data || response.error) {
        throw new Error(
          getResponseErrorMessage({
            error: response.error,
            fallback: "Failed to set up Mem Agent",
          })
        );
      }

      const assistant = response.data;
      const slackSettings = await this.fetchSlackSettings({
        assistantId: assistant.id,
      });

      runInAction(() => {
        this.assistant = assistant;
        this.slackSettings = slackSettings;
      });

      toastModule.triggerToast({
        content: "Mem Agent set up.",
      });
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreMemAgentStore] Failed to set up Mem Agent",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      toastModule.triggerToast({
        content: error.message,
      });
    } finally {
      runInAction(() => {
        this.settingUp = false;
        this.ready = true;
      });
    }
  };

  setAssistantEnabled = async ({ isEnabled }: { isEnabled: boolean }): Promise<void> => {
    if (!this.assistant || this.updatingAssistant) return;

    const previousAssistant = this.assistant;

    runInAction(() => {
      this.updatingAssistant = true;
      this.assistant = {
        ...previousAssistant,
        is_enabled: isEnabled,
      };
    });

    try {
      const response = await assistantApi.patch<MemAgentAssistant>(
        `/v2/assistants/${previousAssistant.id}`,
        {
          body: {
            is_enabled: isEnabled,
          },
        }
      );

      if (!response.data || response.error) {
        throw new Error(
          getResponseErrorMessage({
            error: response.error,
            fallback: "Failed to update Mem Agent",
          })
        );
      }

      const assistant = response.data;

      runInAction(() => {
        this.assistant = assistant;
      });
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreMemAgentStore] Failed to update Mem Agent",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      runInAction(() => {
        this.assistant = previousAssistant;
      });

      toastModule.triggerToast({
        content: error.message,
      });
    } finally {
      runInAction(() => {
        this.updatingAssistant = false;
      });
    }
  };

  connectSlack = async (): Promise<void> => {
    if (!this.assistant || this.connectingSlack) return;

    runInAction(() => {
      this.connectingSlack = true;
    });

    try {
      const response = await assistantApi.post<SlackConnectResponse>(
        `/v2/assistants/${this.assistant.id}/connections/slack/connect`,
        {}
      );

      if (!response.data || response.error) {
        throw new Error(
          getResponseErrorMessage({
            error: response.error,
            fallback: "Failed to start Slack connection",
          })
        );
      }

      localStorageModule.writeSlackOAuthState(response.data.state);

      await windowModule.navigateToExternal({
        url: new URL(response.data.authorization_url),
      });
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreMemAgentStore] Failed to start Slack connection",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      toastModule.triggerToast({
        content: error.message,
      });
    } finally {
      runInAction(() => {
        this.connectingSlack = false;
      });
    }
  };

  setSlackEnabled = async ({ isEnabled }: { isEnabled: boolean }): Promise<void> => {
    if (!this.assistant || !this.slackSettings.is_connected || this.updatingSlack) return;

    const previousSlackSettings = this.slackSettings;

    runInAction(() => {
      this.updatingSlack = true;
      this.slackSettings = {
        ...previousSlackSettings,
        is_enabled: isEnabled,
      };
    });

    try {
      const response = await assistantApi.patch<SlackConnectionSettingsResponse>(
        `/v2/assistants/${this.assistant.id}/connections/slack`,
        {
          body: {
            is_enabled: isEnabled,
          },
        }
      );

      if (!response.data || response.error) {
        throw new Error(
          getResponseErrorMessage({
            error: response.error,
            fallback: "Failed to update Slack connection",
          })
        );
      }

      const slackSettings = response.data;

      runInAction(() => {
        this.slackSettings = slackSettings;
      });
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreMemAgentStore] Failed to update Slack connection",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      runInAction(() => {
        this.slackSettings = previousSlackSettings;
      });

      toastModule.triggerToast({
        content: error.message,
      });
    } finally {
      runInAction(() => {
        this.updatingSlack = false;
      });
    }
  };

  private fetchSlackSettings = async ({
    assistantId,
  }: {
    assistantId: string;
  }): Promise<SlackConnectionSettingsResponse> => {
    const response = await assistantApi.get<SlackConnectionSettingsResponse>(
      `/v2/assistants/${assistantId}/connections/slack`,
      {}
    );

    if (!response.data || response.error) {
      throw new Error(
        getResponseErrorMessage({
          error: response.error,
          fallback: "Failed to load Slack connection",
        })
      );
    }

    return response.data;
  };
}
